import * as React from "react"
import useSession from "@digits-shared/hooks/useSession"
import Session from "@digits-shared/session/Session"
import { developerToolsPreference } from "src/shared/config/developerToolsPreferenceKeys"

const SESSION_EVENTS = [
  Session.USER_PREFERENCES_CHANGE,
  Session.CREATE_EVENT_NAMESPACE,
  Session.UPDATE_EVENT_NAMESPACE,
  Session.DELETE_EVENT_NAMESPACE,
]

export function useDeveloperTools() {
  const session = useSession()
  const subscribe = React.useCallback(
    (onChange: () => void) => {
      SESSION_EVENTS.forEach((event) => session.on(event, onChange))
      return () => SESSION_EVENTS.forEach((event) => session.off(event, onChange))
    },
    [session]
  )
  const getSnapshot = React.useCallback(() => {
    if (!canUseDeveloperTools(session)) return "unavailable"
    return isDeveloperToolsEnabled(session) ? "enabled" : "disabled"
  }, [session])
  const state = React.useSyncExternalStore(subscribe, getSnapshot)
  const setEnabled = React.useCallback(
    (enabled: boolean) => {
      if (canUseDeveloperTools(session)) {
        session.setUserPreference(developerToolsPreference.key, enabled)
      }
    },
    [session]
  )

  return { available: state !== "unavailable", enabled: state === "enabled", setEnabled }
}

export function isDeveloperToolsEnabled(session: Session) {
  return (
    canUseDeveloperTools(session) &&
    session.getBooleanUserPreference(developerToolsPreference.key, false)
  )
}

function canUseDeveloperTools(session: Session) {
  return import.meta.env.DEVELOPER_TOOLS_ENABLED && session.hasUserData && session.isDigitsEmployee
}
